import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { useQueryClient } from '@tanstack/react-query';
import { getReceiverCompanyProducts, logoutUser } from 'lib/api';
import CustomButton from 'lib/designSystem/Button';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from 'services/redux/authSlice';
import { setClientCompanyTypes } from 'services/redux/clientCompanyTypesSlice';
import { ClientConfigState } from 'services/redux/clientConfigSlice';

import { buttonsAvailable } from './buttonsAvailable';

const activeFontColor = '#0F23DF';
const activeWeight = '300';

const useStyles = makeStyles(() =>
  createStyles({
    sidebar: {
      minWidth: 210,
      height: '100vh',
      backgroundColor: 'ffffff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRight: '1px solid #eaecf0',
      overflow: 'hidden',
    },
    logo: {
      padding: '20px 0px 0px',
      textAlign: 'center',
      width: '100%',
      height: '52px',
      borderBottom: '1px solid #eaecf0',
    },
    upItems: {
      width: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    bottomItems: {
      width: '100%',
      flexShrink: 0,
    },
    navList: {
      width: '80%',
    },
    avatarSection: {
      cursor: 'pointer',
      width: '100%',
      padding: '10px',
      overflowX: 'scroll',
      borderTop: '1px solid #eaecf0',
      display: 'flex',
      alignItems: 'center',
    },
    avatarSectionActive: {
      borderTop: `1px solid ${activeFontColor}`,
    },
    avatarActive: {
      color: `${activeFontColor} !important`,
    },
    avatar: {
      width: 45,
      height: 45,
      margin: '0px 12px 0px 0px',
    },
  }),
);

const SideBar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const classes = useStyles();

  const expressConfig = useSelector(
    (state: { clientConfiguration: ClientConfigState }) =>
      state.clientConfiguration.configs,
  );
  const clientCompanyTypes = useSelector(
    (state: { clientCompanyTypes: { types: any[] } }) =>
      state.clientCompanyTypes.types,
  );

  const mainColor = expressConfig?.pruffConnect_styles_mainColor;
  const mail = expressConfig?.pruffConnect_footer_email;

  const StyledListItemButton = styled(ListItemButton)(
    ({ active }: { active: boolean }) => ({
      margin: '6px 8px',
      borderRadius: '16px',
      '& .MuiListItemText-primary': {
        fontSize: 17,
      },
      '&:hover': {
        backgroundColor: '#efefef',
        '& .MuiListItemIcon-root': {
          color: expressConfig?.pruffConnect_styles_mainColor,
        },
        '& .MuiListItemText-primary': {
          fontWeight: activeWeight,
        },
      },
      ...(active && {
        backgroundColor: '#efefef',
        '& .MuiListItemText-primary': {
          fontWeight: activeWeight,
          fontSize: 17,
          color: expressConfig?.pruffConnect_styles_mainColor,
        },
        '& .MuiListItemIcon-root': {
          color: expressConfig?.pruffConnect_styles_mainColor,
        },
      }),
    }),
  );

  const StyledListItemIcon = styled(ListItemIcon)(
    ({ active }: { active: boolean }) => ({
      margin: '0px 10px 0px 0px',
      minWidth: 0,
      '&:hover': {
        color: expressConfig?.pruffConnect_styles_mainColor,
      },
      ...(active && {
        color: expressConfig?.pruffConnect_styles_mainColor,
      }),
    }),
  );

  const handleNavigation = (id: string) => {
    if (id === 'home') {
      navigate('/');
      return;
    }
    navigate(`/${id}`);
  };

  const isActiveButton = (id: string) => {
    if (id === 'home') return location.pathname === '/';
    const currentRoot = location.pathname.split('/')[1];
    const targetRoot = id.split('/')[0];
    return currentRoot === targetRoot;
  };

  const handleLogout = async () => {
    const response = await logoutUser(dispatch);
    if (response.success) {
      queryClient.clear();
      dispatch(logout());
    }
  };

  const fetchClientCompanyTypes = async () => {
    try {
      const response = await getReceiverCompanyProducts(dispatch);
      dispatch(setClientCompanyTypes(response));
    } catch (error) {
      console.error('Error fetching client company types:', error);
    }
  };

  const setNavButtons = () => {
    const navButtons: any = [];
    navButtons.push(buttonsAvailable['home']);
    if (clientCompanyTypes?.some((product) => product.typeId === 'factoring')) {
      navButtons.push(buttonsAvailable['factoring']);
    }
    if (clientCompanyTypes?.some((product) => product.typeId === 'sgr')) {
      navButtons.push(buttonsAvailable['sgr']);
    }
    if (clientCompanyTypes?.some((product) => product.typeId === 'credit')) {
      navButtons.push(buttonsAvailable['credit']);
    }
    if (
      clientCompanyTypes?.some(
        (product) =>
          product.typeId === 'credit' ||
          product.typeId === 'factoring' ||
          product.typeId === 'confirming',
      )
    ) {
      navButtons.push(buttonsAvailable['report']);
    }
    navButtons.push(buttonsAvailable['billing']);
    navButtons.push(buttonsAvailable['settings']);
    const uniqueNavButtons = Array.from(
      new Set(navButtons.map((a: any) => a.label)),
    ).map((label) => {
      return navButtons.find((a: any) => a.label === label);
    });
    return uniqueNavButtons;
  };

  const navButtons = setNavButtons();

  useEffect(() => {
    void fetchClientCompanyTypes();
  }, []);

  return (
    <div className={classes.sidebar}>
      <div className={classes.upItems}>
        <div className={classes.logo}>
          <img
            width={135}
            src={`https://pruff-public.s3.amazonaws.com/company-logos/${expressConfig?.companyId}.png`}
            alt="Logo"
          />
        </div>
        <List
          sx={{
            width: '100%',
            maxWidth: 360,
            bgcolor: 'background.paper',
            paddingTop: 0,
          }}
          component="nav"
          aria-labelledby="nested-list-subheader"
        >
          {navButtons.map((button, index) => {
            const active = isActiveButton(button.id);
            return (
              <div key={index} style={{ position: 'relative' }}>
                <div style={{ padding: '0px 0px' }}>
                  <StyledListItemButton
                    key={index}
                    active={active}
                    onClick={() => handleNavigation(button.id)}
                  >
                    <StyledListItemIcon active={active}>
                      {button.icon}
                    </StyledListItemIcon>
                    <ListItemText primary={button.label} />
                  </StyledListItemButton>
                </div>
              </div>
            );
          })}
        </List>
      </div>
      <div className={classes.bottomItems}>
        <List
          sx={{
            width: '100%',
            maxWidth: 360,
            bgcolor: 'background.paper',
            paddingTop: 0,
          }}
          component="nav"
        >
          <StyledListItemButton
            onClick={() => {
              window.open(`mailto:${mail}?subject=Necesito%20ayuda`, '_blank');
            }}
            active={false}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color={mainColor}>
              ¿Necesitas ayuda?
            </Typography>
          </StyledListItemButton>
          <Box
            pb={2}
            sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <CustomButton
              onClick={() => void handleLogout()}
              style={{ width: '90%' }}
            >
              Cerrar sesión
            </CustomButton>
          </Box>
        </List>
      </div>
    </div>
  );
};

export default SideBar;
